import type { UseFormReturn } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import {
  runsCustomerApp,
  type TenantType,
  type UserType,
  type UserClassification,
  type TenantUserAdminListItem,
  type UpdateTenantUserRequest
} from '@bliks-io/contracts';
import { useAddTenantUser, useUpdateTenantUser } from '@/pages/admin/api/mutations/useTenantUsers';
import { useListTenants } from '@/pages/admin/api/queries/useTenants';
import { ENTITY_ICONS } from '@/constants/entityIcons';
import { FormDialog } from '@/components/dialogs/FormDialog';
import { applyApiErrorToForm } from '@/lib/errors';
import type { ApiError } from '@/lib/agent.types';
import { externalDefaultUserTypes } from '@/lib/user-utils';
import { AddTenantUserFormFields } from './AddTenantUserForm';

export interface AddTenantUserFormData {
  tenantId: string;
  firstName: string;
  lastName: string;
  email: string;
  userTypes: UserType[];
  classification: UserClassification;
  externalOrganization?: string;
  sendInvitation: boolean;
  homeOrgTenantId?: string;
}

interface AddTenantUserDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  tenantId?: string;
  editingUser?: TenantUserAdminListItem;
}

export function AddTenantUserDialog({
  open,
  onOpenChange,
  tenantId,
  editingUser
}: AddTenantUserDialogProps) {
  const { t } = useTranslation();
  const addTenantUser = useAddTenantUser();
  const updateTenantUser = useUpdateTenantUser(tenantId || '');
  const { data: tenantsData, isLoading: isLoadingTenants } = useListTenants();

  const isEditMode = !!editingUser;

  // The tenant's type decides which roles exist and whether classification is
  // offered at all. Undefined while the tenant list loads (or before one is
  // picked) — the form then offers no roles rather than guessing a branch.
  const tenantTypeOf = (id: string | undefined): TenantType | undefined =>
    tenantsData?.tenants.find((tenant) => tenant._id === id)?.tenantType;

  const defaultValues: AddTenantUserFormData = {
    tenantId: tenantId || '',
    firstName: editingUser?.firstName || '',
    lastName: editingUser?.lastName || '',
    email: editingUser?.email || '',
    userTypes: editingUser?.userTypes || [],
    classification: editingUser?.classification || 'internal',
    externalOrganization: editingUser?.externalOrganization || '',
    sendInvitation: false,
    // Only edit mode renders the home-org select; keeping the default equal to
    // the current value means FormDialog's dirty-subset extraction includes
    // homeOrgTenantId in the PATCH only when it actually changed.
    homeOrgTenantId: editingUser?.homeOrgTenantId ?? undefined
  };

  // On edit, FormDialog delivers only the dirty subset of fields (see
  // FormDialog.handleValidSubmit), so any field may be absent at runtime.
  // Validate against the effective (merged-with-existing) state, not just the
  // submitted keys. On create the full, validated form arrives.
  const onSubmit = async (
    data: AddTenantUserFormData,
    form: UseFormReturn<AddTenantUserFormData>
  ) => {
    // Derived, not repaired: a partner tenant has no classification control, so
    // whatever the field happens to hold (a value typed before the tenant type
    // resolved, or a stored `external` from before this rule) must not reach
    // the wire. Deriving here means no handler has to remember to reset it.
    const memberTenantType = tenantTypeOf(data.tenantId ?? tenantId);
    const classification =
      memberTenantType && !runsCustomerApp(memberTenantType)
        ? 'internal'
        : (data.classification ?? editingUser?.classification);
    const externalOrganization =
      'externalOrganization' in data
        ? data.externalOrganization
        : editingUser?.externalOrganization;
    if (classification === 'external' && !externalOrganization) {
      form.setError('externalOrganization', {
        type: 'manual',
        message: t('adminSettings.tenant.externalOrgRequired')
      });
      return;
    }

    try {
      if (isEditMode && editingUser) {
        // Forward the dirty subset; the tenant update applies it via $set.
        // tenantId/sendInvitation aren't rendered in edit mode, so FormDialog's
        // dirty-field extraction never includes them, and the backend request
        // schema strips unknown keys as a second line of defense.
        const update: UpdateTenantUserRequest = { ...data };
        // Sent when it differs from what is stored — which also heals a partner
        // member stored as external, whose classification control is not
        // rendered and could otherwise never be corrected.
        if (classification !== editingUser.classification) {
          update.classification = classification;
        }
        // Switching a member to internal must drop any stored external org —
        // the input unmounts without resetting its value, so it would otherwise
        // linger in the document via $set.
        if (update.classification === 'internal') {
          update.externalOrganization = '';
        }
        await updateTenantUser.mutateAsync({
          id: editingUser._id,
          data: update
        });
      } else {
        const result = await addTenantUser.mutateAsync({
          user: {
            firstName: data.firstName,
            lastName: data.lastName,
            email: data.email,
            userTypes: data.userTypes,
            classification,
            externalOrganization: data.externalOrganization
          },
          tenantId: data.tenantId,
          sendInvitation: data.sendInvitation
        });
        // The backend deliberately doesn't fail the request when only the
        // email fails (the user was created); surface that partial success.
        if (data.sendInvitation && result.invitationSent === false) {
          toast.warning(t('adminSettings.tenant.createdButInvitationFailed'));
        }
      }
      onOpenChange(false);
    } catch (error: unknown) {
      // An already-registered email comes back as a field error and attaches
      // inline to the email input; everything else collapses to a root message,
      // which FormDialog renders in its alert.
      applyApiErrorToForm(error as ApiError, form.setError, t, {
        fields: ['email', 'homeOrgTenantId', 'userTypes']
      });
    }
  };

  return (
    <FormDialog<AddTenantUserFormData>
      open={open}
      onOpenChange={onOpenChange}
      title={
        isEditMode ? t('organization.members.editMember') : t('organization.members.createMember')
      }
      description={
        isEditMode
          ? t('organization.members.editMemberDescription')
          : t('organization.members.createMemberDescription')
      }
      icon={ENTITY_ICONS.orgMember}
      defaultValues={defaultValues}
      onSubmit={onSubmit}
      isEditing={isEditMode}
    >
      {({ form }) => {
        const { control, watch, setValue } = form;
        const classification = watch('classification');
        const tenantType = tenantTypeOf(watch('tenantId'));
        const isExternal =
          tenantType !== undefined && runsCustomerApp(tenantType) && classification === 'external';
        return (
          <AddTenantUserFormFields
            control={control}
            tenantId={tenantId}
            tenantType={tenantType}
            tenantsData={tenantsData}
            isLoadingTenants={isLoadingTenants}
            isExternal={isExternal}
            isEditMode={isEditMode}
            editingUser={editingUser}
            onTenantChange={(value) => {
              setValue('tenantId', value);
              // The role sets of the two app branches are disjoint, so a
              // selection made for the previous tenant cannot survive the
              // switch — it would submit roles the backend now refuses.
              // Classification needs no reset: submit derives it.
              setValue('userTypes', [], { shouldDirty: true });
            }}
            onClassificationChange={(next) => {
              const defaults = externalDefaultUserTypes(next, form.getValues('userTypes'));
              if (defaults) setValue('userTypes', defaults, { shouldDirty: true });
            }}
          />
        );
      }}
    </FormDialog>
  );
}
